// What a person may see and do, and the rules of the one place it is set:
// the person's card in Admin -> Team & Roles (Sahil, 2026-10-19).
//
// Pure: no imports, so the live tests load the very functions the card runs.
// The database is the authority (company_permissions + capability_for, with
// row security and the server routes checking the same capabilities); the
// card writes a person's grants (member:<email>) and the screens follow.
//
// The rules the card keeps:
//  1. A ticked page whose data needs a capability grants it; unticking every
//     page that needs a capability takes it away -- so a person can see what
//     their card shows, and nothing their card does not.
//  2. Capabilities with no page ("Also allowed to") are ticked on the card.
//  3. A role only pre-fills a card (new person, or a role change); saving
//     writes every capability explicitly, so a later change to a role's
//     defaults never shifts a person who is already set up.

export const CAPABILITIES = [
  { id: "read:payments", label: "See payments", help: "Rent and other payments received." },
  { id: "read:accounting", label: "See the books", help: "Journal entries and their lines (the chart of accounts stays visible)." },
  { id: "read:bank", label: "See bank feeds", help: "Bank connections, imported transactions, reconciliations." },
  { id: "read:loans", label: "See loans", help: "Property and portfolio loans." },
  { id: "read:licenses_taxes", label: "See licenses and tax bills", help: "Rental licenses and property tax bills." },
  { id: "read:signatures", label: "See signing records", help: "Who signed what, when, from where." },
  { id: "read:owners_finance", label: "See owners' bank and tax details", help: "Owner bank account, routing and tax id." },
  { id: "decrypt:credentials", label: "View stored logins", help: "Show saved utility, loan, insurance and HOA passwords." },
  { id: "pay:utilities", label: "Pay utilities", help: "Release a utility payment from the company's account." },
  { id: "manage:autopay", label: "Change autopay and saved cards", help: "Turn a tenant's autopay off or replace their card." },
  { id: "void:envelopes", label: "Cancel signature requests", help: "Void a document that is out for signature." },
  { id: "connect:bank", label: "Connect a bank", help: "Link or reconnect the company's bank accounts. Admins only." },
];
// Never granted to anyone but an admin, whatever a card or role says
// (Sahil, 2026-10-23: only admins connect banks). Not on the card.
export const ADMIN_ONLY_CAPS = ["connect:bank"];
export const CAP_IDS = CAPABILITIES.map(c => c.id);
export const capLabel = (id) => (CAPABILITIES.find(c => c.id === id) || {}).label || id;

export const CONFIGURABLE_ROLES = [
  { id: "manager", label: "Manager" },
  { id: "office_assistant", label: "Office Assistant" },
  { id: "accountant", label: "Accountant" },
  { id: "maintenance", label: "Maintenance" },
];

// A page shows only when the capability its data needs is held.
export const PAGE_CAPS = {
  payments: "read:payments",
  accounting: "read:accounting",
  acct_opening: "read:accounting", acct_journal: "read:accounting", acct_recurring: "read:accounting",
  acct_qbimport: "read:accounting", acct_reports: "read:accounting",
  acct_bankimport: "read:bank", acct_reconcile: "read:bank",
  loans: "read:loans",
  tax_bills: "read:licenses_taxes",
  licenses: "read:licenses_taxes",
};
const PAGE_CAP_IDS = Array.from(new Set(Object.values(PAGE_CAPS)));
// The card's "Also allowed to" list: capabilities no page stands for.
export const EXTRA_CAPS = CAPABILITIES.filter(c => !PAGE_CAP_IDS.includes(c.id) && !ADMIN_ONLY_CAPS.includes(c.id));

/** Filter page ids by capabilities (null caps = not loaded yet: show what the role allows). */
export function pagesAllowedByCaps(pages, caps) {
  if (!caps) return pages;
  return pages.filter(p => !PAGE_CAPS[p] || caps[PAGE_CAPS[p]] !== false);
}

// ── what a role / a person holds, as capability_for() answers it ──
// rows: company_permissions rows {subject, capability, allowed};
// defaults: role_capability_defaults() ({role: {cap: bool}}).
const rowValue = (rows, subject, cap) => {
  const r = (rows || []).find(x => String(x.subject).toLowerCase() === subject && x.capability === cap);
  return r ? !!r.allowed : null;
};
export function roleCapabilities(role, rows, defaults) {
  const out = {};
  for (const cap of CAP_IDS) {
    if (role === "admin") { out[cap] = true; continue; }
    if (!role || role === "tenant" || role === "owner") { out[cap] = false; continue; }
    if (ADMIN_ONLY_CAPS.includes(cap)) { out[cap] = false; continue; }
    const own = rowValue(rows, "role:" + role, cap);
    out[cap] = own !== null ? own : !!(defaults && defaults[role] && defaults[role][cap]);
  }
  return out;
}
export function personCapabilities(role, email, rows, defaults) {
  const base = roleCapabilities(role, rows, defaults);
  if (role === "admin" || !role || role === "tenant" || role === "owner") return base;
  const subject = "member:" + String(email || "").trim().toLowerCase();
  const out = {};
  for (const cap of CAP_IDS) { const own = ADMIN_ONLY_CAPS.includes(cap) ? false : rowValue(rows, subject, cap); out[cap] = own !== null ? own : base[cap]; }
  return out;
}

// ── the card's pages ──
// nav: the card's module list ([{id, children?, impliesChildren?}]).
const parentsOf = (nav) => (nav || []).filter(n => n.impliesChildren && n.children && n.children.length);
const parentOf = (nav, id) => parentsOf(nav).find(n => n.children.some(c => c.id === id)) || null;

/** A sub-page that comes with its parent whenever the parent is ticked:
 *  the menu always shows a ticked parent's sub-pages, so only a sub-page
 *  needing a capability of its own (Bank Transactions, Reconcile) can be
 *  switched off under it. */
export function lockedOnCard(nav, pages, id) {
  const p = parentOf(nav, id);
  if (!p || !(pages || []).includes(p.id)) return false;
  return !PAGE_CAPS[id] || PAGE_CAPS[id] === PAGE_CAPS[p.id];
}

/** The pages a card shows for a saved page list. A parent saved without
 *  any of its sub-pages (every card saved before they were listed) means
 *  all of them, as the menu reads it; the locked ones are always there. */
export function cardPages(saved, nav) {
  const set = new Set(saved || []);
  for (const p of parentsOf(nav)) {
    if (!set.has(p.id)) continue;
    const anyChild = p.children.some(c => set.has(c.id));
    for (const c of p.children) {
      if (!anyChild || lockedOnCard(nav, [p.id], c.id)) set.add(c.id);
    }
  }
  return Array.from(set);
}

/** Tick or untick one page on the card. A parent carries its sub-pages; a
 *  sub-page with a capability of its own carries the sub-pages that share
 *  it while the parent is ticked (they show or hide together). */
export function togglePageOnCard(nav, pages, id) {
  const set = new Set(pages || []);
  const on = !set.has(id);
  const parent = parentsOf(nav).find(n => n.id === id);
  if (parent) {
    for (const x of [parent, ...parent.children]) { if (on) set.add(x.id); else set.delete(x.id); }
    return Array.from(set);
  }
  if (lockedOnCard(nav, pages, id)) return Array.from(set);
  const p = parentOf(nav, id);
  const together = p && set.has(p.id) && PAGE_CAPS[id]
    ? p.children.filter(c => PAGE_CAPS[c.id] === PAGE_CAPS[id]).map(c => c.id)
    : [id];
  for (const x of together) { if (on) set.add(x); else set.delete(x); }
  return Array.from(set);
}

/** Capabilities the ticked pages need. */
export function capsNeededBy(pages) {
  return new Set((pages || []).map(p => PAGE_CAPS[p]).filter(Boolean));
}

/** Every capability, explicitly, for the card as ticked (rules 1 and 2). */
export function cardGrants(pages, extras) {
  const need = capsNeededBy(pages);
  const out = {};
  for (const cap of CAP_IDS) out[cap] = PAGE_CAP_IDS.includes(cap) ? need.has(cap) : !!(extras && extras[cap]);
  return out;
}

/** Pre-fill a card from a role: its pages (with the sub-pages listed), less
 *  any page whose data the role does not hold, and its "Also allowed to". */
export function prefillCard(rolePages, roleCaps, nav) {
  const pages = cardPages(rolePages, nav).filter(p => !PAGE_CAPS[p] || (roleCaps && roleCaps[PAGE_CAPS[p]]));
  const extras = {};
  for (const c of EXTRA_CAPS) extras[c.id] = !!(roleCaps && roleCaps[c.id]);
  return { pages, extras };
}

/** Ticked pages the person cannot see today, grouped by the capability
 *  saving will give them: [{cap, pages}]. */
export function notVisibleYet(pages, currentCaps) {
  if (!currentCaps) return [];
  const by = {};
  for (const p of pages || []) {
    const cap = PAGE_CAPS[p];
    if (cap && currentCaps[cap] === false) (by[cap] = by[cap] || []).push(p);
  }
  return Object.entries(by).map(([cap, ps]) => ({ cap, pages: ps }));
}

/** Capabilities the person holds today that saving this card takes away. */
export function savingTakesAway(pages, extras, currentCaps) {
  if (!currentCaps) return [];
  const next = cardGrants(pages, extras);
  return CAP_IDS.filter(cap => currentCaps[cap] === true && next[cap] === false);
}

/** The company_permissions rows a save writes for one person. */
export function memberGrantRows(companyId, email, pages, extras) {
  const subject = "member:" + String(email || "").trim().toLowerCase();
  const g = cardGrants(pages, extras);
  return CAP_IDS.map(cap => ({ company_id: companyId, subject, capability: cap, allowed: g[cap] }));
}
